<template>
  <div :id="name" :ref="(el) => (refs[name].value = el as HTMLInputElement)">
    <div :class="{ 'pointer-events-none cursor-default text-black-30': disabled }">
      <slot name="activator" :is-open="showMenu" :on-click="toggle" />
    </div>

    <Teleport to="body">
      <transition name="fade">
        <div
          v-show="showMenu"
          :id="`${name}_actions`"
          :class="menuClasses"
          class="fixed z-20 min-w-[180px] rounded-xl border-[1.5px] border-solid border-primary-10 bg-white p-1"
        >
          <ol>
            <div class="max-h-[250px] overflow-y-auto">
              <div v-for="(item, index) in items" :key="index">
                <UiTooltip
                  :disabled="!item.tooltip?.text"
                  :name="`action_${index}`"
                  :position-top="item.tooltip?.positionTop"
                  :position-left="item.tooltip?.positionLeft"
                  :position-right="item.tooltip?.positionRight"
                  activator-full-width
                  class="w-full"
                >
                  <template #activator>
                    <div
                      :id="`action_${index}`"
                      :class="item.disabled ? 'text-black-40' : 'hover:bg-primary-05 hover:text-primary-120'"
                      class="text-body-2 flex cursor-pointer select-none flex-row items-center gap-2 whitespace-nowrap p-3"
                      @click="select(item)"
                    >
                      <div v-if="item.icon" class="w-6">
                        <UiIcon :name="item.icon" />
                      </div>
                      {{ item.text }}
                    </div>
                  </template>
                  <template #content>
                    <span> {{ item.tooltip?.text }}</span>
                  </template>
                </UiTooltip>
              </div>
            </div>
          </ol>
        </div>
      </transition>
    </Teleport>
  </div>
</template>

<script setup lang="ts">
import { onClickOutside } from '@vueuse/core'
import type { InputItem } from '@/types'
const emits = defineEmits(['closed', 'action'])

type Props = {
  items: InputItem[]
  name: string
  menuClasses?: string
  alignRight?: boolean
  width?: number
  top?: number
  left?: number
  disabled?: boolean
}
const props = withDefaults(defineProps<Props>(), {
  width: 0,
  menuClasses: '',
  top: undefined,
  left: undefined,
})

const showMenu = ref<boolean>(false)

const refs = {
  [props.name]: ref<HTMLInputElement>(),
}

onMounted(() => {
  onClickOutside(refs[props.name], () => {
    if (showMenu.value) showMenu.value = false
    emits('closed')
  })
})

const toggle = () => {
  showMenu.value = !showMenu.value
  if (!showMenu.value) return
  const el = refs[props.name].value
  if (!el) return
  const viewportOffset = el.getBoundingClientRect()
  // Here we get the element position to the viewport
  const top = props.top || viewportOffset.top
  const left = props.left || viewportOffset.left

  //   Then we set the position of the menu to those coordinates (if theres space)
  //   We do this because we use Teleport to transport to the body of the page
  //   Because otherwise, the menu would be clipped by tables, forms, etc
  //   Source: Trust me
  setTimeout(() => {
    const menu = document.getElementById(`${props.name}_actions`)

    if (menu) {
      const spaceToBottom = window.innerHeight - top

      if (spaceToBottom < menu.offsetHeight + Number(refs[props.name].value?.offsetHeight)) {
        menu.style.top = `${top - menu.offsetHeight}px`
      } else {
        menu.style.top = `${top + Number(refs[props.name].value?.offsetHeight)}px`
      }

      const spaceToRight = window.innerWidth - left

      const elWidth = refs[props.name].value?.offsetWidth ?? 0
      const width = Number(props.width || (elWidth > 180 ? elWidth : 180))

      if (spaceToRight < width || props.alignRight) {
        menu.style.left = `${left - width + Number(refs[props.name].value?.offsetWidth)}px`
      } else {
        menu.style.left = `${left}px`
      }
      menu.style.width = `${width}px`
    }
  })
}

const select = (item: InputItem) => {
  if (item.disabled) return
  emits('action', item.value)
  showMenu.value = false
}

defineSlots<{
  activator(props: { isOpen: boolean; onClick: typeof toggle }): void
}>()

defineExpose({
  open: () => {
    toggle()
  },
  close: () => {
    showMenu.value = false
    emits('closed')
  },
})
</script>

<style scoped></style>
